import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import {
  FiTrendingUp,
  FiUsers,
  FiDollarSign,
  FiClock,
  FiCalendar,
  FiAward,
  FiBarChart2,
  FiFilter,
  FiDownload
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#111827', '#374151', '#6b7280', '#9ca3af', '#d1d5db'];

function StaffPerformance() {
  const [loading, setLoading] = useState(true);
  const [staffPerformance, setStaffPerformance] = useState([]);
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(30);
  const [sortBy, setSortBy] = useState('totalSales'); // 'totalSales', 'billCount', 'performanceScore'
  const [moduleError, setModuleError] = useState(null);

  useEffect(() => {
    fetchStaffPerformance();
  }, [days]);

  const fetchStaffPerformance = async () => {
    try {
      setLoading(true);
      setModuleError(null);
      const response = await api.get(`/api/staff/performance?days=${days}`);
      setStaffPerformance(response.data?.staffPerformance || []);
      setSummary(response.data?.summary || null);
    } catch (error) {
      console.error('Failed to fetch staff performance:', error);
      if (error.response?.status === 403) {
        // Module not enabled
        setModuleError('Staff Performance module is not enabled for your tenant. Please contact your administrator to enable this feature.');
        setStaffPerformance([]);
        setSummary(null);
      } else {
        setModuleError('Failed to load staff performance data. Please try again.');
        setStaffPerformance([]);
        setSummary(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const sortedStaff = useMemo(() => {
    const sorted = [...staffPerformance];
    switch (sortBy) {
      case 'totalSales':
        return sorted.sort((a, b) => b.totalSales - a.totalSales);
      case 'billCount':
        return sorted.sort((a, b) => b.billCount - a.billCount);
      case 'performanceScore':
        return sorted.sort((a, b) => b.performanceScore - a.performanceScore);
      case 'attendanceRate':
        return sorted.sort((a, b) => b.attendanceRate - a.attendanceRate);
      default:
        return sorted;
    }
  }, [staffPerformance, sortBy]);

  const chartData = useMemo(() => {
    return sortedStaff.slice(0, 10).map(staff => ({
      name: staff.name.length > 15 ? staff.name.substring(0, 15) + '...' : staff.name,
      sales: staff.totalSales,
      bills: staff.billCount,
      performance: staff.performanceScore
    }));
  }, [sortedStaff]);

  const attendanceData = useMemo(() => {
    return sortedStaff.map(staff => ({
      name: staff.name.length > 15 ? staff.name.substring(0, 15) + '...' : staff.name,
      attendance: staff.attendanceRate
    }));
  }, [sortedStaff]);

  const topPerformers = useMemo(() => {
    return sortedStaff.slice(0, 3);
  }, [sortedStaff]);

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getPerformanceBadge = (score) => {
    if (score >= 80) return { label: 'Excellent', color: 'bg-green-100 text-green-800' };
    if (score >= 60) return { label: 'Good', color: 'bg-blue-100 text-blue-800' };
    if (score >= 40) return { label: 'Average', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'Needs Improvement', color: 'bg-red-100 text-red-800' };
  };

  if (loading) {
    return (
      <Layout title="Staff Performance">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  // Show error message if module is disabled or other error
  if (moduleError) {
    return (
      <Layout title="Staff Performance Analytics">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md">
            <div className="flex items-center gap-3 mb-2">
              <FiBarChart2 className="h-6 w-6 text-yellow-600" />
              <h2 className="text-lg font-semibold text-yellow-900">Module Not Available</h2>
            </div>
            <p className="text-sm text-yellow-800">{moduleError}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Staff Performance Analytics">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Staff Performance Analytics</h1>
            <p className="text-sm text-gray-600">Track sales, efficiency, and attendance by cashier</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <FiFilter className="text-gray-400" />
              <select
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value))}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
            <button
              onClick={fetchStaffPerformance}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <FiBarChart2 className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Total Staff</span>
                <FiUsers className="text-gray-400" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{summary.totalStaff}</div>
              <p className="text-xs text-gray-500 mt-1">Active cashiers</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Total Sales</span>
                <FiDollarSign className="text-gray-400" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalSales)}</div>
              <p className="text-xs text-gray-500 mt-1">In selected period</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Total Bills</span>
                <FiBarChart2 className="text-gray-400" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{summary.totalBills}</div>
              <p className="text-xs text-gray-500 mt-1">Bills processed</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Avg Sales/Staff</span>
                <FiTrendingUp className="text-gray-400" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(summary.avgSalesPerStaff)}</div>
              <p className="text-xs text-gray-500 mt-1">Per staff member</p>
            </div>
          </div>
        )}

        {/* Top Performers */}
        {topPerformers.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <FiAward className="text-yellow-500" />
              <h2 className="text-lg font-semibold text-gray-900">Top Performers</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {topPerformers.map((staff, index) => (
                <div
                  key={staff.name}
                  className={`p-4 rounded-lg border-2 ${
                    index === 0
                      ? 'border-yellow-400 bg-yellow-50'
                      : index === 1
                      ? 'border-gray-300 bg-gray-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0
                        ? 'bg-yellow-400 text-yellow-900'
                        : index === 1
                        ? 'bg-gray-300 text-gray-900'
                        : 'bg-gray-200 text-gray-700'
                    }`}>
                      {index + 1}
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      getPerformanceBadge(staff.performanceScore).color
                    }`}>
                      {getPerformanceBadge(staff.performanceScore).label}
                    </span>
                  </div>
                  <div className="font-semibold text-gray-900 mb-1">{staff.name}</div>
                  <div className="text-sm text-gray-600 mb-2">{formatCurrency(staff.totalSales)}</div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{staff.billCount} bills</span>
                    <span>{staff.performanceScore.toFixed(1)}% score</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Sales Comparison */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Sales by Staff (Top 10)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#6b7280', fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
                <Bar dataKey="sales" fill="#111827" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Attendance Rate */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Attendance Rate</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={attendanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#6b7280', fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} domain={[0, 100]} />
                <Tooltip
                  formatter={(value) => `${parseFloat(value).toFixed(1)}%`}
                  contentStyle={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
                <Bar dataKey="attendance" fill="#4b5563" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Staff Performance Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Staff Performance Details</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800"
              >
                <option value="totalSales">Total Sales</option>
                <option value="billCount">Bill Count</option>
                <option value="performanceScore">Performance Score</option>
                <option value="attendanceRate">Attendance Rate</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Staff Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Total Sales
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Bills
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Avg Bill Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Days Worked
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Attendance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Avg Turnover
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Performance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedStaff.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-sm text-gray-500">
                      No staff performance data available for the selected period
                    </td>
                  </tr>
                ) : (
                  sortedStaff.map((staff) => {
                    const badge = getPerformanceBadge(staff.performanceScore);
                    return (
                      <tr key={staff.name} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{staff.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {formatCurrency(staff.totalSales)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{staff.billCount}</div>
                          <div className="text-xs text-gray-500">{staff.billsPerDay.toFixed(1)}/day</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatCurrency(staff.avgBillValue)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{staff.daysWorked}</div>
                          <div className="text-xs text-gray-500">
                            {formatCurrency(staff.salesPerDay)}/day
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{staff.attendanceRate.toFixed(1)}%</div>
                          <div className="text-xs text-gray-500">{staff.daysWorked}/{days} days</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {staff.avgTurnoverTime > 0 ? `${staff.avgTurnoverTime.toFixed(1)} min` : 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {staff.uniqueTables} tables
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-semibold text-gray-900">
                              {staff.performanceScore.toFixed(1)}%
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
                              {badge.label}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Peak: {staff.peakHoursPercentage.toFixed(0)}%
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default StaffPerformance;
